import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import CustomTemplateService from "@/services/api/customTemplate";
import type { TemplateModule, TemplateType, TemplateField, TemplateItem, TemplateListResponse } from "@/services/api/customTemplate";
import { handleResponse } from "@/services/axios";

interface CustomTemplateState {
  modules: TemplateModule[];
  typesByModule: Record<number, TemplateType[]>;
  fieldsByType: Record<number, TemplateField[]>;
  templates: TemplateItem[];
  totalTemplates: number;
  isFetching: boolean;
  loadingModuleIds: number[];
  loadingTypeIds: number[];
  error: string | null;
}

const initialState: CustomTemplateState = {
  modules: [],
  typesByModule: {},
  fieldsByType: {},
  templates: [],
  totalTemplates: 0,
  isFetching: false,
  loadingModuleIds: [],
  loadingTypeIds: [],
  error: null,
};

export const fetchTemplates = createAsyncThunk(
  "customTemplate/fetchTemplates",
  async (payload: { from?: number, to?: number, text?: string} = {}, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.getTemplates(payload);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch templates");
    }
  }
);

export const fetchModules = createAsyncThunk(
  "customTemplate/fetchModules",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.getModules();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch modules");
    }
  }
);

export const fetchTemplateTypes = createAsyncThunk(
  "customTemplate/fetchTemplateTypes",
  async (moduleId: number, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.getTemplateTypes(moduleId);
      return { moduleId, data: handleResponse(response, rejectWithValue) };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch template types");
    }
  }
);

export const fetchTemplateFields = createAsyncThunk(
  "customTemplate/fetchTemplateFields",
  async ({ typeId, moduleId }: { typeId: number, moduleId: number }, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.getTemplateFields(typeId, moduleId);
      return { typeId, data: handleResponse(response, rejectWithValue) };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch template fields");
    }
  }
);

export const saveTemplateData = createAsyncThunk(
  "customTemplate/saveTemplateData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.saveTemplate(payload);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to save template");
    }
  }
);

export const fetchTemplateById = createAsyncThunk(
  "customTemplate/fetchTemplateById",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.getTemplateById(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch template details");
    }
  }
);

export const deleteTemplate = createAsyncThunk(
  "customTemplate/deleteTemplate",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await CustomTemplateService.deleteTemplate(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to delete template");
    }
  }
);

const customTemplateSlice = createSlice({

  name: "customTemplate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Modules
      .addCase(fetchModules.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.isFetching = false;
        state.modules = action.payload || [];
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      // Fetch Template Types
      .addCase(fetchTemplateTypes.pending, (state, action) => {
        state.isFetching = true;
        const moduleId = action.meta.arg;
        if (!state.loadingModuleIds.includes(moduleId)) {
          state.loadingModuleIds.push(moduleId);
        }
      })
      .addCase(fetchTemplateTypes.fulfilled, (state, action) => {
        state.isFetching = false;
        state.loadingModuleIds = state.loadingModuleIds.filter((id) => id !== action.payload.moduleId);
        state.typesByModule[action.payload.moduleId] = action.payload.data || [];
      })
      .addCase(fetchTemplateTypes.rejected, (state, action) => {
        state.isFetching = false;
        state.loadingModuleIds = state.loadingModuleIds.filter((id) => id !== action.meta.arg);
        state.error = action.payload as string;
      })
      // Fetch Template Fields
      .addCase(fetchTemplateFields.pending, (state, action) => {
        state.isFetching = true;
        const typeId = action.meta.arg.typeId;
        if (!state.loadingTypeIds.includes(typeId)) {
          state.loadingTypeIds.push(typeId);
        }
      })
      .addCase(fetchTemplateFields.fulfilled, (state, action) => {
        state.isFetching = false;
        state.loadingTypeIds = state.loadingTypeIds.filter((id) => id !== action.payload.typeId);
        state.fieldsByType[action.payload.typeId] = action.payload.data || [];
      })
      .addCase(fetchTemplateFields.rejected, (state, action) => {
        state.isFetching = false;
        state.loadingTypeIds = state.loadingTypeIds.filter((id) => id !== action.meta.arg.typeId);
        state.error = action.payload as string;
      })
      // Fetch Templates
      .addCase(fetchTemplates.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.isFetching = false;
        const items = action.payload?.Items || [];
        const isAppend = (action.meta.arg?.from || 0) > 1;
        
        if (isAppend) {
          state.templates = [...state.templates, ...items];
        } else {
          state.templates = items;
        }
        state.totalTemplates = action.payload?.TotalRecords || 0;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      // Fetch Template By Id
      .addCase(fetchTemplateById.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchTemplateById.fulfilled, (state) => {
        state.isFetching = false;
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      });
  },
});

export default customTemplateSlice.reducer;
